"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { getLocaleMeta } from "@/lib/i18n/locales";
import {
  computeFactorial,
  type FactorialComputation,
  type FactorialView,
  factorialLines,
  type GammaView,
} from "@/lib/tool-kit/arithmetic/factorial-view";
import { scientificFromDigits } from "@/lib/tool-kit/arithmetic/power";

/** English strings. A translated content file overrides these through `ui`. */
export const FACTORIAL_CALCULATOR_STRINGS = {
  nLabel: "Number, n",
  nHint:
    "A whole number from 0 to 10,000. A decimal such as 4.5 uses the gamma function.",
  results: "Results",
  factorial: "n!",
  digitsCount: "Digits in n!",
  trailingZeros: "Trailing zeros",
  doubleFactorial: "Double factorial, n!!",
  scientific: "Scientific notation",
  stirling: "Stirling’s approximation",
  stirlingError: "How far below n! it falls",
  correctedError: "Error with the 1/(12n) correction",
  gamma: "Γ(n + 1)",
  gammaNote:
    "n is not a whole number, so the factorial is extended by the gamma function: n! = Γ(n + 1). For a whole number it gives the usual factorial.",
  allDigits: "All the digits",
  copyDigits: "Copy digits",
  copyResult: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  zeroRule:
    "0! = 1. There is exactly one way to arrange nothing, and an empty product equals 1. This also keeps n! = n × (n − 1)! true for n = 1.",
  chain: "{n}! = {chain} = {value}.",
  recursion:
    "{n}! = {first} × {second} × … × 2 × 1 = {n} × {m}!, so each factorial is the one before it multiplied by {n}.",
  trailing:
    "Each trailing zero comes from a factor of 10 = 2 × 5, and fives are scarcer than twos. Counting them: ⌊{n}/5⌋ + ⌊{n}/25⌋ + … = {sum}.",
  trailingNone:
    "{n} is below 5, so {n}! has no factor of 5 and no trailing zeros.",
  doubleLine:
    "The double factorial multiplies every second number: {n}!! = {chain} = {value}.",
  stirlingLine:
    "Stirling’s formula √(2πn) (n/e)ⁿ gives about {value}, which is {error} below the exact value.",
  enterNumber: "Enter a number to see its factorial.",
  invalid: "That is not a number. Try 5, 20 or 100.",
  negative:
    "The factorial is not defined for negative whole numbers (the gamma function has poles there). Try a non-negative number.",
  tooBig:
    "Factorials above 10,000! have over 35,000 digits. Enter a number up to 10,000.",
  gammaOverflow:
    "Γ(n + 1) overflows a double-precision number above n ≈ 170. Use a whole number to get the exact value.",
} as const;

type Strings = typeof FACTORIAL_CALCULATOR_STRINGS;

const FULL_DIGITS = 24;
const DIGIT_BOX =
  "max-h-56 overflow-auto break-all rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm leading-relaxed";

function useBigFormatter(): (value: bigint) => string {
  const locale = useToolLocale();
  return (value: bigint) => {
    const digits = value.toString();
    if (digits.length > FULL_DIGITS) {
      const sci = scientificFromDigits(digits, 8);
      return `≈ ${sci.mantissa} × 10^${sci.exponent}`;
    }
    try {
      const tag = getLocaleMeta(locale)?.hreflang ?? "en-US";
      return value.toLocaleString(tag);
    } catch {
      return digits;
    }
  };
}

function WholeResults({
  view,
  strings,
}: {
  view: FactorialView;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const fmtBig = useBigFormatter();
  const percent = (value: number) =>
    `${fmt(value, { maximumSignificantDigits: 3 })}%`;
  const long = view.digitCount > FULL_DIGITS;
  const lines = factorialLines(view, strings, percent, fmtBig);
  const sci = view.scientific;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.factorial}
          value={fmtBig(view.value)}
        />
        <StatCard label={strings.digitsCount} value={fmt(view.digitCount)} />
        <StatCard label={strings.trailingZeros} value={fmt(view.zeros)} />
        <StatCard label={strings.doubleFactorial} value={fmtBig(view.double)} />
        {long ? (
          <StatCard
            label={strings.scientific}
            value={`${sci.mantissa} × 10^${sci.exponent}`}
          />
        ) : null}
        {view.stirling ? (
          <>
            <StatCard
              label={strings.stirling}
              value={`≈ ${view.stirling.mantissa} × 10^${view.stirling.exponent}`}
            />
            <StatCard
              label={strings.stirlingError}
              value={percent(Math.abs(view.stirling.relativeError) * 100)}
            />
            <StatCard
              label={strings.correctedError}
              value={percent(
                Math.abs(view.stirling.correctedRelativeError) * 100
              )}
            />
          </>
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={`${view.n}! = ${view.digits}`}
      />
      {long ? (
        <>
          <p className="font-medium text-foreground text-sm">
            {strings.allDigits}
          </p>
          <p className={DIGIT_BOX}>{view.digits}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyDigits}
            small
            text={view.digits}
          />
        </>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </>
  );
}

function GammaResults({
  view,
  strings,
}: {
  view: GammaView;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const value = fmt(view.value, { maximumSignificantDigits: 12 });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.gamma} value={`≈ ${value}`} />
      </StatGrid>
      <Notice>{strings.gammaNote}</Notice>
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: Exclude<FactorialComputation["kind"], "whole" | "fractional">;
  strings: Strings;
}) {
  switch (kind) {
    case "empty":
      return <Notice>{strings.enterNumber}</Notice>;
    case "invalid":
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
    case "negative":
      return <ErrorNotice>{strings.negative}</ErrorNotice>;
    case "too-big":
      return <ErrorNotice>{strings.tooBig}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.gammaOverflow}</ErrorNotice>;
  }
}

function Outcome({
  outcome,
  strings,
}: {
  outcome: FactorialComputation;
  strings: Strings;
}) {
  if (outcome.kind === "whole") {
    return <WholeResults strings={strings} view={outcome.view} />;
  }
  if (outcome.kind === "fractional") {
    return <GammaResults strings={strings} view={outcome.view} />;
  }
  return <Problem kind={outcome.kind} strings={strings} />;
}

export default function FactorialCalculatorWidget() {
  const strings = useToolUi(FACTORIAL_CALCULATOR_STRINGS);
  const [raw, setRaw] = useState("20");
  // A 10,000-digit-scale product takes a few milliseconds; deferring keeps typing smooth.
  const deferred = useDeferredValue(raw);
  const outcome = useMemo(() => computeFactorial(deferred), [deferred]);
  return (
    <>
      <div className="max-w-sm">
        <TextField
          hint={strings.nHint}
          id="fc-n"
          label={strings.nLabel}
          mono
          numeric
          onChange={setRaw}
          value={raw}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome outcome={outcome} strings={strings} />
      </section>
    </>
  );
}
